import { ExternalLink, GitMerge, ImageOff, Scissors, Sparkles, X } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { type AutoMergeMode, TUNABLES } from "../../shared/tunables";
import { StockBadge } from "../components/StockBadge";
import { fmtRelativeTime } from "../lib/format";
import { proxiedImageUrl } from "../lib/imageProxy";
import { useTunables } from "../lib/tunables";
import { navigate } from "../router";
import { Button, EmptyState, ErrorState, HowItWorks, Money, Page, Point } from "../ui";
import { SkeletonList } from "../ui/Skeleton";
import { useWs } from "../ws";

type Kind = "missed" | "suspect";

type Member = {
	productId: number;
	storeId: string;
	title: string;
	normalizedTitle: string | null;
	ean: string | null;
	imageUrl: string | null;
	priceMinor: number | null;
	inStock: boolean | null;
	url: string;
	groupKey: string;
};

type Candidate = {
	kind: Kind;
	/** Which rule proposed this: an exact title collision, or the model. */
	via: "title" | "attributes";
	signature: string;
	members: Member[];
};

/** Tabs, which are the two candidate queues plus the findings view. */
type Tab = Kind | "findings" | "auto";

type AutoMergeEntry = {
	id: number;
	signature: string;
	via: "title" | "attributes";
	groupKey: string;
	members: Array<{ id: number; storeId: string; title: string; prev: string | null }>;
	mergedAt: number;
	undoneAt: number | null;
};

type ExtractState = {
	running: boolean;
	model: string | null;
	pending: number;
	total: number;
	extracted: number;
	identified: number;
	failedChunks: number;
	cost: number | null;
	lastError: string | null;
	cancelled: boolean;
};

type Attributes = {
	line: string | null;
	set: string | null;
	productType: string | null;
	language: string | null;
	edition: string | null;
};

type Finding = {
	productId: number;
	storeId: string;
	title: string;
	url: string;
	attributeKey: string | null;
	attributes: Attributes | null;
	model: string | null;
	extractedAt: number;
};

type FindingFilter = "all" | "identified" | "unidentified";

async function fetchCandidates(kind: Kind): Promise<Candidate[]> {
	const res = await fetch(`/api/grouping/review?kind=${kind}`);
	if (!res.ok) throw new Error(`HTTP ${res.status}`);
	const data = (await res.json()) as { candidates: Candidate[] };
	return data.candidates;
}

async function postJson(path: string, body: unknown): Promise<unknown> {
	const res = await fetch(path, {
		method: "POST",
		headers: { "content-type": "application/json" },
		body: JSON.stringify(body),
	});
	if (!res.ok) {
		const j = (await res.json().catch(() => ({}))) as { error?: string };
		throw new Error(j.error ?? `HTTP ${res.status}`);
	}
	return res.json();
}

/**
 * Manual, bounded trigger for attribute extraction, and the live view of one.
 *
 * The run lives on the server, so this panel reports rather than drives: it
 * reads the current state on mount and then follows the websocket. Reloading
 * the page, or opening a second tab, shows the same run rather than an idle
 * button over a job that is spending money.
 */
function ExtractPanel({
	onProgress,
	refreshToken,
}: {
	onProgress: () => void;
	/** Bumped when a finding is cleared, which puts a product back in line. */
	refreshToken: number;
}) {
	const [state, setState] = useState<ExtractState | null>(null);
	const [limit, setLimit] = useState<number | "all">(25);
	const [error, setError] = useState<string | null>(null);

	const refresh = useCallback(
		() =>
			fetch("/api/grouping/extract")
				.then((r) => r.json())
				.then(setState)
				.catch(() => setState(null)),
		[],
	);

	useEffect(() => {
		void refresh();
	}, [refresh, refreshToken]);

	useWs((msg) => {
		if (msg.type === "extract.started") {
			setState((s) =>
				s
					? {
							...s,
							running: true,
							total: msg.total,
							extracted: 0,
							identified: 0,
							failedChunks: 0,
							cost: null,
							lastError: null,
							cancelled: false,
						}
					: s,
			);
			return;
		}
		if (msg.type === "extract.progress") {
			setState((s) => (s ? { ...s, running: true, ...msg, pending: Math.max(0, s.pending - 0) } : s));
			// New keys may have produced new candidates; refresh the list beneath.
			onProgress();
			return;
		}
		if (msg.type === "extract.done") {
			setState((s) => (s ? { ...s, ...msg, running: false } : s));
			void refresh();
			onProgress();
		}
	});

	const start = async () => {
		setError(null);
		const res = await fetch("/api/grouping/extract", {
			method: "POST",
			headers: { "content-type": "application/json" },
			body: JSON.stringify({ limit }),
		});
		const data = (await res.json()) as { ok: boolean; error?: string };
		if (!data.ok) {
			setError(data.error ?? `HTTP ${res.status}`);
			return;
		}
		await refresh();
	};

	const cancel = async () => {
		await fetch("/api/grouping/extract/cancel", { method: "POST" });
		await refresh();
	};

	const noModel = state != null && state.model == null;
	const pending = state?.pending ?? 0;
	const running = state?.running ?? false;
	const willDo = limit === "all" ? pending : Math.min(limit, pending);

	// One row on a wide screen: the name, what a run would do, and the
	// controls. The prose wraps rather than pushing the controls off the end.
	return (
		<section className="mb-3 rounded-[10px] border border-hairline bg-paper px-3 py-2">
			<div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-4">
				<div className="flex shrink-0 items-center gap-1.5">
					<Sparkles className="size-3.5 text-muted" aria-hidden />
					<h2 className="text-[13px] font-semibold text-ink">AI matching</h2>
					{running && <span className="text-[11px] font-medium text-muted">Running</span>}
				</div>

				{running && state ? (
					<>
						<div className="flex min-w-0 flex-1 flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3">
							<div className="w-full shrink-0 sm:w-40">
								<Progress done={state.extracted + state.failedChunks * 0} total={state.total} />
							</div>
							<p className="min-w-0 text-xs text-muted">
								{state.extracted} of {state.total} read · {state.identified} identified
								{state.failedChunks > 0 &&
									` · ${state.failedChunks} chunk${state.failedChunks === 1 ? "" : "s"} failed`}
								{state.cost != null && ` · $${state.cost.toFixed(4)} so far`}
								<span className="text-muted/80"> · Saved as it goes — leaving this page will not stop it.</span>
							</p>
						</div>
						<Button variant="secondary" onClick={cancel} className="shrink-0 self-start lg:self-auto">
							Stop
						</Button>
					</>
				) : (
					<>
						<p className="min-w-0 flex-1 text-xs leading-snug text-muted">
							{noModel
								? "Pick a model under Manage → Settings to enable this."
								: state == null
									? "Checking…"
									: `${pending} product${pending === 1 ? "" : "s"} to read: listings with no EAN first, then one listing per EAN group from the stores where EANs go missing, so those listings can find their group. Reads each title, category and description with ${state.model}, ten at a time, and proposes matches below. Nothing merges on its own.`}
						</p>
						<div className="flex shrink-0 items-center gap-1.5">
							<select
								value={String(limit)}
								onChange={(e) => setLimit(e.target.value === "all" ? "all" : Number(e.target.value))}
								disabled={noModel}
								aria-label="How many to read"
								className="h-8 min-h-tap rounded-md border border-hairline bg-ground px-2.5 text-[13px] text-ink disabled:opacity-50"
							>
								{[25, 50, 100].map((n) => (
									<option key={n} value={n}>
										{n} at a time
									</option>
								))}
								<option value="all">All remaining</option>
							</select>
							<Button
								variant="secondary"
								onClick={start}
								disabled={noModel || pending === 0}
								className="flex-1 sm:flex-initial"
							>
								Extract {willDo}
							</Button>
						</div>
					</>
				)}
			</div>

			{!running && state && state.total > 0 && state.extracted + state.failedChunks > 0 && (
				<p className="mt-1 text-[11px] text-muted">
					Last run: {state.extracted} read · {state.identified} identified
					{state.failedChunks > 0 && ` · ${state.failedChunks} chunk${state.failedChunks === 1 ? "" : "s"} failed`}
					{state.cancelled && " · stopped"}
					{state.cost != null && ` · $${state.cost.toFixed(4)}`}
				</p>
			)}
			{!running && state?.lastError && (
				// Now carries the API's own words, which can be a long line of
				// JSON — wrap it rather than letting it run off the card.
				<p className="mt-1 text-[11px] break-words text-fault">{state.lastError}</p>
			)}
			{error && <p className="mt-1 text-[11px] text-fault">{error}</p>}
		</section>
	);
}

function Progress({ done, total }: { done: number; total: number }) {
	const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
	return (
		<div
			role="progressbar"
			aria-valuenow={done}
			aria-valuemin={0}
			aria-valuemax={total}
			className="h-1.5 w-full overflow-hidden rounded-full bg-ground"
		>
			<div className="h-full bg-ink transition-[width] duration-300" style={{ width: `${pct}%` }} />
		</div>
	);
}

/**
 * What the model made of each title. Counts alone cannot answer "why did
 * this not group?" — the answer is the attributes it came back with.
 */
function FindingsList({ onChange }: { onChange: () => void }) {
	const [filter, setFilter] = useState<FindingFilter>("all");
	const [rows, setRows] = useState<Finding[] | null>(null);
	const [counts, setCounts] = useState<Record<FindingFilter, number> | null>(null);
	const [busy, setBusy] = useState<number | null>(null);

	const load = useCallback(
		(f: FindingFilter) =>
			fetch(`/api/grouping/findings?filter=${f}&limit=200`)
				.then((r) => r.json())
				.then((d: { findings: Finding[]; counts: Record<FindingFilter, number> }) => {
					setRows(d.findings);
					setCounts(d.counts);
				}),
		[],
	);

	useEffect(() => {
		setRows(null);
		void load(filter);
	}, [filter, load]);

	const clear = async (productId: number) => {
		setBusy(productId);
		try {
			await fetch("/api/grouping/findings/clear", {
				method: "POST",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({ productId }),
			});
			await load(filter);
			// The product is pending again, so the panel's count above is stale.
			onChange();
		} finally {
			setBusy(null);
		}
	};

	return (
		<div>
			<Segmented label="Filter findings" className="mb-2">
				{(["all", "identified", "unidentified"] as const).map((f) => (
					<TabButton
						key={f}
						active={filter === f}
						onClick={() => setFilter(f)}
						label={`${f === "all" ? "All" : f === "identified" ? "Identified" : "Not identified"}${counts ? ` ${counts[f]}` : ""}`}
					/>
				))}
			</Segmented>

			{rows == null ? (
				<SkeletonList rows={6} rowHeight={40} />
			) : rows.length === 0 ? (
				<EmptyState
					icon={Sparkles}
					title="Nothing read yet."
					description="Run AI matching above and what the model made of each title will appear here."
				/>
			) : (
				// One line per finding on a wide screen — store, title, then what the
				// model read — so a long run can be scanned down a single column.
				<ul className="divide-y divide-hairline rounded-[10px] border border-hairline bg-paper">
					{rows.map((f) => (
						<li key={f.productId} className="flex items-center gap-2 px-3 py-1.5 hover:bg-raised">
							<div className="flex min-w-0 flex-1 flex-col gap-0.5 lg:flex-row lg:items-center lg:gap-3">
								<div className="flex min-w-0 items-center gap-1.5 lg:w-[45%] lg:shrink-0">
									<span className="shrink-0 rounded-full bg-raised px-2 py-0.5 text-[11px] font-medium text-muted">
										{f.storeId}
									</span>
									<a href={`#/items/${f.productId}`} className="truncate text-[13px] text-ink hover:underline">
										{f.title}
									</a>
								</div>
								{f.attributeKey ? (
									<p className="min-w-0 font-mono text-[11px] break-all text-muted lg:truncate">
										{f.attributeKey.split("|").join(" · ")}
									</p>
								) : (
									<p className="min-w-0 text-[11px] text-muted lg:truncate">
										Not identified
										{f.attributes && (f.attributes.line || f.attributes.set) && (
											<span>
												{" "}
												— got{" "}
												{[f.attributes.line, f.attributes.set, f.attributes.productType].filter(Boolean).join(" · ") ||
													"nothing"}
											</span>
										)}
									</p>
								)}
							</div>
							<Button
								variant="ghost"
								size="sm"
								onClick={() => void clear(f.productId)}
								disabled={busy === f.productId}
								className="shrink-0"
							>
								{busy === f.productId ? "…" : "Clear"}
							</Button>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}

export function GroupingReview() {
	const [tab, setTab] = useState<Tab>("missed");
	const [refreshToken, setRefreshToken] = useState(0);
	const kind: Kind = tab === "findings" || tab === "auto" ? "missed" : tab;
	const [candidates, setCandidates] = useState<Candidate[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState<string | null>(null);
	const reqIdRef = useRef(0);

	const load = useCallback(
		async (k: Kind = kind) => {
			const reqId = ++reqIdRef.current;
			setError(null);
			try {
				const rows = await fetchCandidates(k);
				if (reqId !== reqIdRef.current) return;
				setCandidates(rows);
			} catch (e) {
				if (reqId !== reqIdRef.current) return;
				setError((e as Error).message ?? "failed to load");
			}
		},
		[kind],
	);

	useEffect(() => {
		if (tab === "findings" || tab === "auto") return;
		setCandidates(null);
		void load(tab);
	}, [tab, load]);

	const merge = async (c: Candidate) => {
		setBusy(c.signature);
		try {
			await postJson("/api/grouping/merge", {
				productIds: c.members.map((m) => m.productId),
			});
			await load(kind);
		} catch (e) {
			setError((e as Error).message);
		} finally {
			setBusy(null);
		}
	};

	const reject = async (c: Candidate) => {
		setBusy(c.signature);
		try {
			await postJson("/api/grouping/reject", { signature: c.signature });
			await load(kind);
		} catch (e) {
			setError((e as Error).message);
		} finally {
			setBusy(null);
		}
	};

	const unlink = async (c: Candidate, productId: number) => {
		setBusy(c.signature);
		try {
			await postJson("/api/grouping/unlink", { productId });
			await load(kind);
		} catch (e) {
			setError((e as Error).message);
		} finally {
			setBusy(null);
		}
	};

	return (
		<Page
			title="Grouping review"
			help={
				<HowItWorks>
					<Point lead="Most grouping is automatic.">
						Listings with the same EAN (the barcode number) are one item, in whatever store they are. Items and alerts
						compare prices across that group.
					</Point>
					<Point lead="Missed matches">
						are listings from different stores that look like the same item but sit apart, because their titles are
						identical or the AI read them the same way. Merge them into one group, or choose Not the same so they stop
						coming back.
					</Point>
					<Point lead="Suspect merges">
						are EAN groups where one store lists several different products under the same code, like colours or
						characters of a range. Split out the ones that don't belong, or choose Looks fine.
					</Point>
					<Point lead="AI matching">
						reads the listings EANs can't help with and works out the product line, set, type, language and quantity.
						Listings that come out the same are proposed as missed matches. It costs a little per listing, runs only
						when you start it, and never merges anything by itself. AI findings shows what it read, and lets you throw
						away a bad reading.
					</Point>
					<Point lead="It can run on its own.">
						In Settings, the AI can read new listings every 10 minutes, and matches can merge themselves when they pass
						strict checks: at most one EAN, no store listing them separately, prices within the limit set there, nothing
						split out by hand. Auto-merged lists every one, with Undo.
					</Point>
					<Point lead="A wrong merge">
						can be fixed with Split out on the item's page, or Undo under Auto-merged if auto-merge made it.
					</Point>
				</HowItWorks>
			}
			subtitle="Confirm merges across stores, or split bad EAN groups."
			width="full"
		>
			<AutomationLine />

			<ExtractPanel
				refreshToken={refreshToken}
				onProgress={() => {
					if (tab === "missed") void load("missed");
				}}
			/>

			<Segmented label="Review queue" className="mb-3">
				<TabButton active={tab === "missed"} onClick={() => setTab("missed")} label="Missed matches" />
				<TabButton active={tab === "suspect"} onClick={() => setTab("suspect")} label="Suspect merges" />
				<TabButton active={tab === "findings"} onClick={() => setTab("findings")} label="AI findings" />
				<TabButton active={tab === "auto"} onClick={() => setTab("auto")} label="Auto-merged" />
			</Segmented>

			{error && (
				<div className="mb-3">
					<ErrorState message={error} onRetry={() => void load(kind)} />
				</div>
			)}

			{tab === "findings" ? (
				<FindingsList onChange={() => setRefreshToken((n) => n + 1)} />
			) : tab === "auto" ? (
				<AutoMergeList />
			) : candidates == null ? (
				<SkeletonList rows={3} rowHeight={120} />
			) : candidates.length === 0 ? (
				<EmptyState
					icon={GitMerge}
					title={kind === "missed" ? "No missed matches to review." : "No suspect merges to review."}
					description={
						kind === "missed"
							? "Variants that match across stores but aren't already grouped will appear here — by identical title, or through AI matching above."
							: "Groups where one store lists several differently titled products under the same EAN will appear here, like colours or characters sharing one code."
					}
				/>
			) : (
				<ul className="flex flex-col gap-2">
					{candidates.map((c) => (
						<li key={c.signature} className="rounded-[10px] border border-hairline bg-paper p-3">
							<CandidateCard
								candidate={c}
								busy={busy === c.signature}
								onMerge={() => void merge(c)}
								onReject={() => void reject(c)}
								onUnlink={(pid) => void unlink(c, pid)}
							/>
						</li>
					))}
				</ul>
			)}
		</Page>
	);
}

const MERGE_WORDS: Record<AutoMergeMode, string> = {
	off: "off",
	title: "identical titles",
	title_ai: "identical titles and AI matches",
};

/** What runs on its own, in one line, with the way to change it. */
function AutomationLine() {
	const { get } = useTunables();
	const reading = get(TUNABLES.autoExtract);
	const mode = get(TUNABLES.autoMerge);
	return (
		<p className="mb-2 px-0.5 text-xs text-muted">
			Automatic reading is{" "}
			<span className="text-ink">
				{reading ? `on, up to ${get(TUNABLES.autoExtractPerHour)} listings an hour` : "off"}
			</span>
			. Automatic merging is <span className="text-ink">{MERGE_WORDS[mode]}</span>.{" "}
			<a href="#/manage/settings" className="text-ink underline underline-offset-2 hover:no-underline">
				Change in Settings
			</a>
		</p>
	);
}

/** Every merge auto-merge made, newest first, each with a way back. */
function AutoMergeList() {
	const [entries, setEntries] = useState<AutoMergeEntry[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState<number | "run" | null>(null);
	const [ran, setRan] = useState<string | null>(null);
	const { get } = useTunables();

	const load = useCallback(async () => {
		try {
			const res = await fetch("/api/grouping/auto-merges");
			const d = (await res.json()) as { merges: AutoMergeEntry[] };
			setEntries(d.merges);
		} catch (e) {
			setError((e as Error).message);
		}
	}, []);

	useEffect(() => {
		void load();
	}, [load]);

	const undo = async (id: number) => {
		setBusy(id);
		try {
			await postJson(`/api/grouping/auto-merges/${id}/undo`, {});
			await load();
		} catch (e) {
			setError((e as Error).message);
		} finally {
			setBusy(null);
		}
	};

	const runNow = async () => {
		setBusy("run");
		try {
			const r = (await postJson("/api/grouping/auto-merges/run", {})) as {
				merged: number;
				skipped: number;
			};
			setRan(`Merged ${r.merged}, left ${r.skipped} that didn't pass the checks for you to review.`);
			await load();
		} catch (e) {
			setError((e as Error).message);
		} finally {
			setBusy(null);
		}
	};

	const off = get(TUNABLES.autoMerge) === "off";

	return (
		<div>
			<div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
				<Button variant="secondary" onClick={() => void runNow()} disabled={off || busy === "run"}>
					{busy === "run" ? "Merging…" : "Run auto-merge now"}
				</Button>
				<span className="text-xs text-muted">
					{off ? "Turn automatic merging on in Settings to use this." : (ran ?? "It also runs every 10 minutes.")}
				</span>
			</div>
			{error && (
				<div className="mb-3">
					<ErrorState message={error} onRetry={() => void load()} />
				</div>
			)}
			{entries == null ? (
				<SkeletonList rows={3} rowHeight={72} />
			) : entries.length === 0 ? (
				<EmptyState
					icon={GitMerge}
					title="Nothing merged automatically yet."
					description="Merges auto-merge makes are listed here, so you can check them and undo any that are wrong."
				/>
			) : (
				<ul className="divide-y divide-hairline rounded-[10px] border border-hairline bg-paper">
					{entries.map((e) => (
						<li key={e.id} className="flex items-start gap-3 px-3 py-2">
							<div className="min-w-0 flex-1">
								<p className="text-[11px] text-muted">
									{fmtRelativeTime(e.mergedAt)}, by {e.via === "title" ? "identical title" : "AI match"}
									{e.undoneAt != null && <span className="text-fault">, undone</span>}
								</p>
								<ul className="mt-1 grid gap-x-4 gap-y-0.5 xl:grid-cols-2">
									{e.members.map((m) => (
										<li key={m.id} className="flex min-w-0 items-center gap-1.5 text-[13px]">
											<span className="shrink-0 rounded-full bg-raised px-2 py-0.5 text-[11px] font-medium text-muted">
												{m.storeId}
											</span>
											<a
												href={`#/items/${m.id}`}
												className={`truncate hover:underline ${
													e.undoneAt != null ? "text-muted line-through" : "text-ink"
												}`}
											>
												{m.title}
											</a>
										</li>
									))}
								</ul>
							</div>
							{e.undoneAt == null && (
								<Button
									variant="secondary"
									size="sm"
									onClick={() => void undo(e.id)}
									disabled={busy === e.id}
									className="shrink-0"
								>
									{busy === e.id ? "Undoing…" : "Undo"}
								</Button>
							)}
						</li>
					))}
				</ul>
			)}
		</div>
	);
}

/**
 * A compact segmented control. It scrolls sideways rather than wrapping
 * when the tabs don't fit a phone.
 */
function Segmented({ label, className = "", children }: { label: string; className?: string; children: ReactNode }) {
	return (
		<div className={`flex max-w-full overflow-x-auto ${className}`}>
			<div
				role="group"
				aria-label={label}
				className="inline-flex shrink-0 rounded-lg border border-hairline bg-ground p-0.5"
			>
				{children}
			</div>
		</div>
	);
}

function TabButton({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-pressed={active}
			className={`min-h-tap rounded-md px-2.5 text-xs whitespace-nowrap sm:h-7 sm:min-h-0 ${
				active ? "bg-paper font-semibold text-ink shadow-[0_1px_2px_rgba(6,9,18,0.12)]" : "text-muted hover:text-ink"
			}`}
		>
			{label}
		</button>
	);
}

function CandidateCard({
	candidate,
	busy,
	onMerge,
	onReject,
	onUnlink,
}: {
	candidate: Candidate;
	busy: boolean;
	onMerge: () => void;
	onReject: () => void;
	onUnlink: (productId: number) => void;
}) {
	const norm = new Set(candidate.members.map((m) => m.normalizedTitle).filter((t): t is string => t != null));
	const sharedTitle = norm.size === 1 ? [...norm][0] : null;

	// The decision sits on the header line, beside what is being decided, so a
	// candidate costs one line plus its members rather than a footer row too.
	return (
		<div className="flex flex-col gap-2">
			<div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
				<div className="flex min-w-[12rem] flex-1 items-center gap-2 text-xs text-muted">
					<span className="shrink-0 font-medium text-ink">
						{candidate.members.length} variant
						{candidate.members.length === 1 ? "" : "s"}
					</span>
					{sharedTitle && <span className="min-w-0 truncate text-ink">"{sharedTitle}"</span>}
					{candidate.via === "attributes" && (
						<span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-raised px-2 py-0.5 text-[11px] font-medium text-muted">
							<Sparkles className="size-3" aria-hidden />
							AI
						</span>
					)}
					<span className="min-w-0 truncate font-mono text-[10px] text-muted" title={candidate.signature}>
						{candidate.signature}
					</span>
				</div>
				<div className="flex shrink-0 flex-wrap items-center gap-1.5">
					{candidate.kind === "missed" && (
						<Button type="button" variant="primary" onClick={onMerge} disabled={busy}>
							<GitMerge className="size-3.5" aria-hidden />
							Merge into one group
						</Button>
					)}
					<Button type="button" variant="secondary" onClick={onReject} disabled={busy}>
						<X className="size-3.5" aria-hidden />
						{candidate.kind === "missed" ? "Not the same" : "Looks fine"}
					</Button>
				</div>
			</div>

			<ul className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
				{candidate.members.map((m) => (
					<li
						key={m.productId}
						className="flex items-start gap-2 rounded-lg border border-hairline bg-paper p-2 hover:border-line"
					>
						<Thumbnail src={m.imageUrl} alt={m.title} />
						<div className="min-w-0 flex-1">
							<div className="flex min-w-0 items-center gap-1.5">
								<span className="shrink-0 rounded-full bg-raised px-2 py-0.5 text-[11px] font-medium text-muted">
									{m.storeId}
								</span>
								{m.ean && <span className="truncate font-mono text-[10px] text-muted">EAN {m.ean}</span>}
							</div>
							<p className="mt-0.5 line-clamp-2 text-[13px] leading-snug text-ink" title={m.title}>
								{m.title}
							</p>
							<div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
								{m.inStock != null && <StockBadge inStock={m.inStock} />}
								{m.priceMinor != null && <Money minor={m.priceMinor} className="text-[13px] text-ink" />}
								<span className="ml-auto flex items-center gap-2">
									{candidate.kind === "suspect" && (
										// Stays hand-rolled: a dense inline action inside a
										// compact variant card, on one line with the price and
										// links — even Button's sm size (h-7) is taller than
										// the rest of that line.
										<button
											type="button"
											onClick={() => onUnlink(m.productId)}
											disabled={busy}
											className="inline-flex h-6 items-center gap-1 rounded-md border border-hairline px-1.5 font-medium text-ink hover:border-line hover:bg-raised disabled:opacity-50 pointer-coarse:h-8 pointer-coarse:px-2.5"
											title="Pull this variant out of the group"
										>
											<Scissors className="size-3" aria-hidden /> Split out
										</button>
									)}
									<button
										type="button"
										onClick={() => navigate(`/items/${m.productId}`)}
										className="text-muted hover:text-ink hover:underline"
									>
										View item
									</button>
									<a
										href={m.url}
										target="_blank"
										rel="noreferrer noopener"
										className="inline-flex items-center gap-0.5 text-muted hover:text-ink hover:underline"
									>
										Open <ExternalLink className="size-3" aria-hidden />
									</a>
								</span>
							</div>
						</div>
					</li>
				))}
			</ul>
		</div>
	);
}

function Thumbnail({ src, alt }: { src: string | null; alt: string }) {
	return (
		<div className="size-10 shrink-0 overflow-hidden rounded-md border border-hairline bg-paper">
			{src ? (
				<img src={proxiedImageUrl(src) ?? undefined} alt={alt} loading="lazy" className="size-full object-cover" />
			) : (
				<div className="flex size-full items-center justify-center text-muted">
					<ImageOff className="size-4" aria-hidden />
				</div>
			)}
		</div>
	);
}
